import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./ExportVideoModal.css";
import { API_BASE_URL } from "../../../Common/Common";

// Output formats. 9:16 added 2026-10-10 for Shorts/Reels/TikTok — every
// draw function already takes w/h, so the frame size is the main switch;
// maps are fetched in the matching shape and captions get a bottom safe
// zone (see bottomInset).
const FORMATS = {
  landscape: { w: 1920, h: 1080, label: "16:9 (YouTube, TV)" },
  portrait:  { w: 1080, h: 1920, label: "9:16 (Shorts, Reels, TikTok)" },
};

// Shorts/Reels/TikTok overlay their caption, buttons and progress bar on the
// bottom of a vertical video — keep our captions above that. 15% of the
// height plus one caption line (~70px): at 15% alone, TikTok's own caption
// text still overlapped our date line (2026-10-11). Landscape unaffected.
const CAPTION_LINE_PX = 70;
function bottomInset(w, h) {
  return h > w ? Math.round(h * 0.15) + CAPTION_LINE_PX : 0;
}
const LABEL_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const STATIC_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_STATIC_API;

// 1.5s added 2026-10-10 for fast-paced Shorts/Reels; 2s is the default.
const DURATION_OPTIONS = [1.5, 2, 3, 5, 8];
const AUDIO_TRACKS = [
  { id: "none",      label: "None"      },
  { id: "peaceful",  label: "Peaceful"  },
  { id: "cinematic", label: "Cinematic" },
  { id: "adventure", label: "Adventure" },
  { id: "nostalgic", label: "Nostalgic" },
];
const MAP_OPTIONS = [
  { id: "off",   label: "Off"      },
  { id: "start", label: "At start" },
  { id: "end",   label: "At end"   },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const IMG_PROXY = `${API_BASE_URL}/img_proxy.php?url=`;

function resolveImg(url) {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("gdrive:")) return `https://lh3.googleusercontent.com/d/${url.slice(7)}`;
  // Normalize relative liquidguides filenames to a full URL.
  if (!url.includes("http")) url = `http://liquidguides.com/Media/Photos/${url}`;
  // Route any http:// image through the proxy — blocked as mixed content on HTTPS pages.
  if (url.startsWith("http://")) return IMG_PROXY + encodeURIComponent(url);
  return url;
}

// Fallback used when resolveImg's URL (e.g. the img_proxy for http:// sources)
// fails to load — matches what PlaceCard/PlaceDetails use directly via
// Common.getImageURL, so anything that displays there also recovers here
// instead of showing a broken-image icon.
function directImg(url) {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("gdrive:")) return `https://lh3.googleusercontent.com/d/${url.slice(7)}`;
  if (!url.includes("http")) url = `http://liquidguides.com/Media/Photos/${url}`;
  return url.startsWith("http://") ? url.replace("http://", "https://") : url;
}

function buildStaticMapUrl(places, portrait = false) {
  const pins = places.filter(p => p.PType === "Icon" && p.Lat && p.Longi);
  if (!pins.length || !STATIC_MAP_KEY) return "";
  const markers = pins
    .map((p, i) => `&markers=size:mid%7Ccolor:red%7Clabel:${LABEL_CHARS[i % LABEL_CHARS.length]}%7C${p.Lat},${p.Longi}`)
    .join("");
  return `https://maps.googleapis.com/maps/api/staticmap?size=${portrait ? "360x640" : "640x360"}&scale=2&maptype=roadmap${markers}&key=${STATIC_MAP_KEY}`;
}

function formatSlideDate(dt) {
  if (!dt) return "";
  try {
    if (typeof dt.toFormat === "function") return dt.toFormat("LLLL d, yyyy");
    const d = new Date(dt);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
      .toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

const sleep = ms => new Promise(r => setTimeout(r, ms));

// Holds a slide on screen by redrawing it every REDRAW_MS instead of drawing
// once and sleeping through the rest of the duration. Chrome's VP9 encoder,
// fed a canvas.captureStream() that barely changes, treats near-zero motion
// as "doesn't need many bits" and collapses the actual encoded bitrate to a
// small fraction of the requested videoBitsPerSecond — measured on a real
// export: ~107kbps actual vs. an 8,000,000 target, visibly blocky/blurry
// output despite the correct 1920x1080 resolution. Periodically redrawing
// identical pixels keeps captureStream() emitting fresh frames, which is
// enough to make the encoder treat it as continuous content and actually
// respect something close to the requested bitrate. Found 2026-09-23.
const REDRAW_MS = 200;
async function holdFrame(drawFn, totalMs) {
  let elapsed = 0;
  while (elapsed < totalMs) {
    drawFn();
    const step = Math.min(REDRAW_MS, totalMs - elapsed);
    await sleep(step);
    elapsed += step;
  }
}

// ── Audio engine (Web Audio procedural synthesis) ────────────────────────────

function createAudioEngine(trackId) {
  if (trackId === "none") return { stream: null, stop: () => {} };
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return { stream: null, stop: () => {} };

  const ctx = new Ctx();
  const master = ctx.createGain();
  master.gain.value = 0.12;
  const dest = ctx.createMediaStreamDestination();

  // Simple space: two delays + low-pass
  const d1 = ctx.createDelay(0.65); d1.delayTime.value = 0.28;
  const d2 = ctx.createDelay(1.25); d2.delayTime.value = 0.55;
  const rg = ctx.createGain(); rg.gain.value = 0.18;
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1100;
  master.connect(d1); d1.connect(d2); d2.connect(rg); rg.connect(lp);
  lp.connect(dest); master.connect(dest);

  // Track definitions
  const DEFS = {
    // Peaceful: D-A arpeggio, slow and meditative
    peaceful: {
      seqs: [[293.66, 440, 587.33, 440]],
      noteDur: 3.5, atk: 1.0, rel: 1.5, wave: "sine",
    },
    // Cinematic: slow Am–Dm–C chord progression
    cinematic: {
      seqs: [[220, 261.63, 329.63], [174.61, 220, 261.63], [261.63, 329.63, 392.0]],
      noteDur: 7.0, atk: 1.8, rel: 2.2, wave: "sine", chord: true,
    },
    // Adventure: bright G-major pentatonic run
    adventure: {
      seqs: [[392, 493.88, 587.33, 659.25, 587.33, 493.88]],
      noteDur: 1.2, atk: 0.05, rel: 0.4, wave: "triangle",
    },
    // Nostalgic: A minor pentatonic, slow with long decay
    nostalgic: {
      seqs: [[220, 261.63, 329.63, 392, 329.63, 261.63]],
      noteDur: 2.5, atk: 0.04, rel: 1.8, wave: "sine",
    },
  };

  const cfg = DEFS[trackId] || DEFS.peaceful;
  let si = 0, ni = 0, stopped = false;

  function tick() {
    if (stopped) return;
    const now = ctx.currentTime;
    const seq = cfg.seqs[si % cfg.seqs.length];
    const freqs = cfg.chord ? (si++, seq) : [seq[(ni++) % seq.length]];

    freqs.forEach(freq => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = cfg.wave;
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(1, now + cfg.atk);
      g.gain.setValueAtTime(1, now + cfg.noteDur);
      g.gain.linearRampToValueAtTime(0, now + cfg.noteDur + cfg.rel);
      osc.connect(g); g.connect(master);
      osc.start(now); osc.stop(now + cfg.noteDur + cfg.rel + 0.1);
    });

    setTimeout(tick, cfg.noteDur * 1000);
  }
  tick();

  return {
    stream: dest.stream,
    stop: () => {
      stopped = true;
      master.gain.setTargetAtTime(0, ctx.currentTime, 0.5);
      setTimeout(() => ctx.close().catch(() => {}), 2000);
    },
  };
}

// ── Canvas drawing ────────────────────────────────────────────────────────────

function drawCover(ctx, img, w, h) {
  const iAR = img.width / img.height, cAR = w / h;
  let sx, sy, sw, sh;
  if (iAR > cAR) { sh = img.height; sw = sh * cAR; sx = (img.width - sw) / 2; sy = 0; }
  else           { sw = img.width;  sh = sw / cAR;  sx = 0; sy = (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
}

function drawOverlay(ctx, w, h, name, dateStr) {
  const b = bottomInset(w, h);
  const g = ctx.createLinearGradient(0, h * 0.45, 0, h);
  g.addColorStop(0, "rgba(0,0,0,0)");
  g.addColorStop(1, "rgba(0,0,0,0.78)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  const maxW = w - 96;
  ctx.font = "bold 58px Arial,sans-serif";
  ctx.fillStyle = "#fff";
  ctx.textBaseline = "bottom";
  ctx.textAlign = "left";
  let n = name;
  while (ctx.measureText(n).width > maxW && n.length > 1) n = n.slice(0, -1);
  ctx.fillText(n !== name ? n + "…" : n, 48, h - 56 - b);

  if (dateStr) {
    ctx.font = "30px Arial,sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.82)";
    ctx.fillText(dateStr, 50, h - 20 - b);
  }

  ctx.font = "18px Arial,sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText("Andiamo Places", w - 20, 16);
}

function drawMapOverlay(ctx, w) {
  const g = ctx.createLinearGradient(0, 0, 0, 90);
  g.addColorStop(0, "rgba(0,0,0,0.65)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, 90);
  ctx.font = "bold 36px Arial,sans-serif";
  ctx.fillStyle = "#fff";
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillText("Trip Map", 40, 20);
  ctx.font = "18px Arial,sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.textAlign = "right";
  ctx.fillText("Andiamo Places", w - 20, 20);
}

function drawDayMapOverlay(ctx, w, h, dateStr) {
  const b = bottomInset(w, h);
  const g = ctx.createLinearGradient(0, h * 0.5, 0, h);
  g.addColorStop(0, "rgba(0,0,0,0)");
  g.addColorStop(1, "rgba(0,0,0,0.75)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  ctx.font = "bold 58px Arial,sans-serif";
  ctx.fillStyle = "#fff";
  ctx.textBaseline = "bottom";
  ctx.textAlign = "left";
  ctx.fillText(dateStr, 48, h - 56 - b);

  ctx.font = "30px Arial,sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.fillText("Day Overview", 50, h - 20 - b);

  ctx.font = "18px Arial,sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText("Andiamo Places", w - 20, 16);
}

function drawPlaceholder(ctx, w, h, name, dateStr) {
  const b = bottomInset(w, h);
  const bg = ctx.createLinearGradient(0, 0, w, h);
  bg.addColorStop(0, "#1e3a5f");
  bg.addColorStop(1, "#0f172a");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, w, h);

  // Location pin icon
  const cx = w / 2, cy = h / 2 - 60, r = 52;
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(59,130,246,0.35)"; ctx.fill();
  ctx.beginPath(); ctx.arc(cx, cy, r * 0.52, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(147,197,253,0.75)"; ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.65, cy + r * 0.1);
  ctx.lineTo(cx + r * 0.65, cy + r * 0.1);
  ctx.lineTo(cx, cy + r * 1.1);
  ctx.closePath();
  ctx.fillStyle = "rgba(59,130,246,0.35)"; ctx.fill();

  const fg = ctx.createLinearGradient(0, h * 0.5, 0, h);
  fg.addColorStop(0, "rgba(0,0,0,0)");
  fg.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = fg;
  ctx.fillRect(0, 0, w, h);

  const maxW = w - 96;
  ctx.font = "bold 58px Arial,sans-serif";
  ctx.fillStyle = "#fff";
  ctx.textBaseline = "bottom";
  ctx.textAlign = "left";
  let n = name;
  while (ctx.measureText(n).width > maxW && n.length > 1) n = n.slice(0, -1);
  ctx.fillText(n !== name ? n + "…" : n, 48, h - 56 - b);

  if (dateStr) {
    ctx.font = "30px Arial,sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.fillText(dateStr, 50, h - 20 - b);
  }
  ctx.font = "18px Arial,sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.3)";
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText("Andiamo Places", w - 20, 16);
}

function drawEndCard(ctx, img, w, h) {
  if (img) {
    drawCover(ctx, img, w, h);
    const ov = ctx.createLinearGradient(0, 0, 0, h);
    ov.addColorStop(0, "rgba(0,0,0,0.45)"); ov.addColorStop(1, "rgba(0,0,0,0.68)");
    ctx.fillStyle = ov; ctx.fillRect(0, 0, w, h);
  } else {
    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0, "#1e3a5f"); bg.addColorStop(1, "#0f172a");
    ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
  }
  // Upper third, not dead center — leaves room below for whatever's in the
  // photo's own lower half, and matches how video players tend to overlay
  // controls/scrubbers near the vertical center on playback.
  const textY = h * 0.32;
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.font = "bold 64px Arial,sans-serif";
  ctx.fillStyle = "#fff";
  ctx.fillText("Created by Andiamo Places", w / 2, textY);
  ctx.font = "36px Arial,sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText("AndiamoPlaces.com", w / 2, textY + 68);
}

// ── Slide list ────────────────────────────────────────────────────────────────

function dayKey(dt) {
  try {
    if (typeof dt.toFormat === "function") return dt.toFormat("yyyy-MM-dd");
    const d = new Date(dt);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  } catch { return String(dt); }
}

function buildSlides(places, events, mapPos, dailyMaps) {
  const evList = events || [];

  if (dailyMaps && evList.length > 0) {
    // Build a day → {firstStart, placeIds} map, preserving day order
    const dayOrder = [];
    const dayData  = {};
    evList.forEach(ev => {
      if (!ev.Start) return;
      const key = dayKey(ev.Start);
      if (!dayData[key]) { dayData[key] = { start: ev.Start, placeIds: [] }; dayOrder.push(key); }
      (ev.Places || "").split(",").forEach(id => {
        id = id.trim();
        if (id && !dayData[key].placeIds.includes(id)) dayData[key].placeIds.push(id);
      });
    });

    const scheduledIds = new Set();
    const slides = [];

    dayOrder.forEach(key => {
      const { start, placeIds } = dayData[key];
      const dayPlaces = placeIds.map(id => places.find(p => String(p.ID) === id)).filter(Boolean);
      if (!dayPlaces.length) return;
      dayPlaces.forEach(p => scheduledIds.add(String(p.ID)));
      slides.push({ type: "daymap", places: dayPlaces, date: start });
      dayPlaces.forEach(p => slides.push({ type: "place", place: p, date: start }));
    });

    places.filter(p => !scheduledIds.has(String(p.ID)))
          .forEach(p => slides.push({ type: "place", place: p, date: null }));

    const mSlide = { type: "map", place: null, date: null };
    if (mapPos === "start") return [mSlide, ...slides];
    if (mapPos === "end")   return [...slides, mSlide];
    return slides;
  }

  // Flat sort (no daily maps)
  const dateMap = {};
  evList.forEach(ev => {
    if (!ev.Start) return;
    (ev.Places || "").split(",").forEach(id => {
      id = id.trim();
      if (id && (!dateMap[id] || ev.Start < dateMap[id])) dateMap[id] = ev.Start;
    });
  });
  const dated   = places.filter(p =>  dateMap[String(p.ID)]);
  const undated = places.filter(p => !dateMap[String(p.ID)]);
  dated.sort((a, b) => {
    const da = dateMap[String(a.ID)], db = dateMap[String(b.ID)];
    return da < db ? -1 : da > db ? 1 : 0;
  });
  const pSlides = [...dated, ...undated].map(p => ({ type: "place", place: p, date: dateMap[String(p.ID)] || null }));
  const mSlide  = { type: "map", place: null, date: null };
  if (mapPos === "start") return [mSlide, ...pSlides];
  if (mapPos === "end")   return [...pSlides, mSlide];
  return pSlides;
}

// Same bounded-concurrency pattern as PlacesHeader.js's ENRICHMENT_CONCURRENCY
// worker pool, applied here for the same reason: Google Drive's
// lh3.googleusercontent.com/d/{fileId} link format (what gdrive: photos
// resolve to) is known to throttle/reject requests when many are fired at
// once from the same client — real-world symptom: a burst of "empty blue
// screen" placeholder slides in an export with a lot of Drive-sourced
// photos, none of them actually broken individually. Loading images in
// small batches instead of one unbounded Promise.all keeps this well clear
// of that. Found 2026-09-24.
const IMAGE_LOAD_CONCURRENCY = 4;
async function loadImagesBounded(urls, imgCache, onProgress) {
  let nextIndex = 0;
  let completed = 0;
  const worker = async () => {
    while (nextIndex < urls.length) {
      const i = nextIndex++;
      const url = urls[i];
      imgCache[url] = await loadImg(url);
      completed++;
      if (onProgress) onProgress(completed, urls.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(IMAGE_LOAD_CONCURRENCY, urls.length) }, worker));
}

async function loadImg(url) {
  if (!url) return null;
  const tryLoad = (src, cors) => new Promise(resolve => {
    const img = new Image();
    if (cors) img.crossOrigin = "anonymous";
    img.onload  = () => resolve(img);
    img.onerror = () => resolve(null);
    setTimeout(() => resolve(null), 8000);
    img.src = src;
  });
  // 1. Try CORS-clean (ideal — keeps canvas untainted).
  const img1 = await tryLoad(url, true);
  if (img1) return img1;
  // 2. Browser may have cached this without CORS headers; retry with cache-bust.
  const sep = url.includes("?") ? "&" : "?";
  const img2 = await tryLoad(`${url}${sep}_cv=${Date.now()}`, true);
  if (img2) return img2;
  // 3. Try via server-side proxy (bypasses CORS restrictions on external hosts).
  const proxyUrl = `${API_BASE_URL}/img_proxy.php?url=${encodeURIComponent(url)}`;
  const img3 = await tryLoad(proxyUrl, true);
  if (img3) return img3;
  // 4. Last resort: load without crossOrigin. The canvas will be tainted but
  //    captureStream() is exempt from the origin-dirty restriction (it doesn't
  //    expose pixel data to script), so the video still captures correctly.
  return tryLoad(url, false);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ExportVideoModal({ show, onHide }) {
  const guide_meta = useSelector(s => s.guide_meta);
  const canvasRef  = useRef(null);
  const recRef     = useRef(null);
  const chunksRef  = useRef([]);
  const audioRef   = useRef(null);
  const stopWatchdogRef = useRef(null);

  const [phase,        setPhase]        = useState("settings");
  const [format,       setFormat]       = useState("landscape");
  const [duration,     setDuration]     = useState(2);
  const [audioTrack,   setAudioTrack]   = useState("none"); // default no sound (2026-10-10)
  const [mapPos,       setMapPos]       = useState("end");
  const [dailyMaps,    setDailyMaps]    = useState(true);
  const [skipNoPhoto,  setSkipNoPhoto]  = useState(true);
  const [titleSlide,   setTitleSlide]   = useState(true);
  const [titleBg,      setTitleBg]      = useState("photo");
  const [titleText,    setTitleText]    = useState("");
  const [endCard,      setEndCard]      = useState(true);
  const titleTextRef = useRef("");
  const [fileBaseName, setFileBaseName] = useState("trip-video");
  const [allPlaces,    setAllPlaces]    = useState([]);
  const [selIds,       setSelIds]       = useState(new Set());
  const [progress,     setProgress]     = useState(0);
  const [progLabel,    setProgLabel]    = useState("");
  const [blobUrl,      setBlobUrl]      = useState(null);
  const [fileName,     setFileName]     = useState("trip-video.webm");
  const [failedImgs,   setFailedImgs]   = useState(0);
  const [totalImgSlides, setTotalImgSlides] = useState(0);
  // Surfaces a real "why" instead of hanging on "generating" forever — see
  // handleGenerate's own mimeType-resolution comment for the concrete bug
  // this was found from (Safari/iOS has neither WebM/VP9 nor an unqualified
  // "video/mp4" MediaRecorder, so the old code's fallback silently
  // constructed a MediaRecorder with a mimeType Safari can't record,
  // throwing with no error handling anywhere in this function). Found
  // 2026-09-30.
  const [errorMsg,     setErrorMsg]     = useState("");
  // Frame size for the chosen format — used by every draw call and the
  // <canvas> itself.
  const { w: CANVAS_W, h: CANVAS_H } = FORMATS[format];
  const portrait = format === "portrait";

  // Build place→earliest-event-date map once per open
  const dateMap = useMemo(() => {
    const map = {};
    (window.guide_obj?.schedule?.events || []).forEach(ev => {
      if (!ev.Start) return;
      (ev.Places || "").split(",").forEach(id => {
        id = id.trim();
        if (id && (!map[id] || ev.Start < map[id])) map[id] = ev.Start;
      });
    });
    return map;
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!show) return;
    setPhase("settings");
    setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setProgress(0); setProgLabel("");
    const guide = window.guide_obj;
    if (!guide) return;
    const places = (guide.places?.placesList || [])
      .filter(p => !p.Hidden && p.PType !== "PolyLine" && p.PType !== "Polygon");
    setAllPlaces(places);
    setSelIds(new Set(places.map(p => String(p.ID))));
    // The Guide model's field is guide_name (Guide.js's constructor/loaders
    // set this.guide_name, never .name or .guide_info.name/.name) — same
    // field HomeHeader.js reads via guide_meta.guide_info.guide_name. The
    // previous lookup here (.guide_info?.name / .name / guide_meta's .name)
    // never matched anything real, so title/filename silently defaulted to
    // blank/"trip-video" instead of the guide's actual name. Found 2026-09-24.
    // attributes.guide_name first: guide.guide_name doesn't exist on a Guide, and
    // guide_meta can still describe the previous guide while a new one is unsaved
    // (2026-10-24, matches mobile ExportVideoScreen.js).
    const guideName = guide?.attributes?.guide_name || guide_meta?.guide_info?.guide_name || "";
    setTitleText(guideName);
    titleTextRef.current = guideName;
    setFileBaseName(
      (guideName || "trip").replace(/[^a-z0-9]/gi, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").toLowerCase().slice(0, 60) || "trip-video"
    );
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlace = id => setSelIds(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  const handleGenerate = useCallback(async () => {
    const guide  = window.guide_obj;
    const canvas = canvasRef.current;
    if (!guide || !canvas) return;

    const selected = allPlaces.filter(p => selIds.has(String(p.ID)) && (!skipNoPhoto || !!resolveImg(p.Photo_m || p.Photo)));
    const slides   = buildSlides(selected, guide.schedule?.events || [], mapPos, dailyMaps);
    if (!slides.length) return;

    setPhase("generating");
    setProgress(0);
    setErrorMsg("");
    // Let React actually flush the phase change into the DOM (the canvas's
    // CSS class switches from an off-screen position to visible/full-size —
    // see .ev-canvas--on) and let the browser paint it at least once before
    // capturing — WebKit's canvas.captureStream() is known to deliver no
    // real frames from a canvas that hasn't actually been composited
    // on-screen yet, even though Chrome/Firefox tolerate capturing
    // immediately. Two RAF ticks (not one) to be past the paint, not just
    // the next layout. Found 2026-09-30 alongside the 0-byte-file bug.
    await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

    const ctx2d = canvas.getContext("2d");

    const audio = createAudioEngine(audioTrack);
    audioRef.current = audio;

    // Safari/iOS (desktop and mobile — "iOS Chrome" is the same WebKit
    // engine under Apple's rules) supports neither WebM nor an unqualified
    // "video/mp4" in MediaRecorder.isTypeSupported — it needs an explicit
    // H.264/AAC codec string. The old 3-candidate list (webm;vp9 / webm /
    // mp4) matched nothing on Safari, so .find() returned undefined and the
    // "|| \"video/webm\"" fallback silently fed MediaRecorder a mimeType
    // Safari can't record at all, throwing with no try/catch anywhere in
    // this function — the export just hung on "generating" forever with no
    // visible error. Found 2026-09-30 (confirmed working on Android Chrome,
    // failing silently on iOS Safari/Chrome). No "|| " fallback now — if
    // genuinely nothing matches, that's surfaced as a real error instead of
    // handed a doomed mimeType.
    const MIME_CANDIDATES = [
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
      "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
      "video/mp4;codecs=h264,aac",
      "video/mp4",
    ];
    const mimeType = MIME_CANDIDATES.find(t => window.MediaRecorder && MediaRecorder.isTypeSupported(t));
    if (!mimeType) {
      setErrorMsg("This browser can't record video in a supported format. Try Chrome or Firefox on desktop or Android.");
      setPhase("settings");
      audio.stop();
      return;
    }
    const ext  = mimeType.startsWith("video/mp4") ? "mp4" : "webm";
    const base = (fileBaseName.trim() || "trip-video") + (portrait ? "-9x16" : "");
    setFileName(`${base}.${ext}`);

    let recorder;
    try {
      const videoTrack = canvas.captureStream(30).getVideoTracks()[0];
      const allTracks  = audio.stream
        ? [videoTrack, ...audio.stream.getAudioTracks()]
        : [videoTrack];
      const stream = new MediaStream(allTracks);
      recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 8_000_000 });
    } catch (err) {
      console.error("ExportVideoModal: MediaRecorder construction failed", err);
      setErrorMsg("Couldn't start video recording in this browser. Try Chrome or Firefox on desktop or Android.");
      setPhase("settings");
      audio.stop();
      return;
    }
    recRef.current = recorder;
    chunksRef.current = [];

    // stopWatchdogRef guards against onstop never firing at all (observed
    // 2026-09-30: iOS Chrome's progress reached ~98% — every slide drawn,
    // recorder.stop() called — and then hung forever) and finishBlob
    // against a genuinely empty result (observed on iOS Safari: onstop DID
    // fire and a file DID download, but 0 bytes — no real frames were ever
    // captured). Either way the user now gets an explicit error instead of
    // an infinite spinner or a broken silent "success".
    let stopHandled = false;
    const finishBlob = () => {
      if (stopHandled) return;
      stopHandled = true;
      clearTimeout(stopWatchdogRef.current);
      const blob = new Blob(chunksRef.current, { type: mimeType });
      if (blob.size === 0) {
        setErrorMsg("The recording came out empty (0 bytes) — this browser's video capture isn't working correctly. Try Chrome or Firefox on desktop or Android.");
        setPhase("settings");
        return;
      }
      setBlobUrl(URL.createObjectURL(blob));
      setPhase("done");
    };
    recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = finishBlob;

    // ── Preload all images before recording so sleep() = actual screen time ──
    setProgLabel("Loading images…");
    // Guide's own cover photo — same source the title slide already uses.
    // The end card previously used the site's static Facebook/Twitter share
    // image (public/og-image-v4.jpg), but that's a 1200x630 marketing card
    // with "Andiamo Places"/tagline/platform-pill text already baked into
    // its center — it visibly collided with this card's own attribution
    // text, and got blurry upscaled to the 1920x1080 canvas. The guide cover
    // photo has neither problem and bookends the video (opens and closes on
    // the same shot). Found 2026-09-23.
    // Guides store their cover as image_m (medium) / image (thumbnail) on
    // attributes / guide_info — this used to read only `photo`/`Photo`, which
    // guides don't have, so the title and end cards always fell back to the
    // plain blue background even with "Photo" chosen. Fixed 2026-10-11;
    // old field names kept as a last fallback.
    const coverPhotoUrl = resolveImg(
      guide.attributes?.image_m || guide_meta?.guide_info?.image_m ||
      guide.attributes?.image || guide_meta?.guide_info?.image ||
      guide.guide_info?.photo || guide.guide_info?.Photo || guide_meta?.guide_info?.photo
    );
    const titleBgUrl   = titleSlide && titleBg === "photo" ? coverPhotoUrl : null;
    const endCardBgUrl = endCard ? coverPhotoUrl : null;
    const allUrls = new Set();
    if (titleBgUrl) allUrls.add(titleBgUrl);
    if (endCardBgUrl) allUrls.add(endCardBgUrl);
    slides.forEach(s => {
      if (s.type === "place") {
        const u = resolveImg(s.place.Photo_m || s.place.Photo);
        if (u) allUrls.add(u);
      } else if (s.type === "map" || s.type === "daymap") {
        const pins = s.type === "map" ? allPlaces.filter(p => selIds.has(String(p.ID))) : s.places;
        const u = buildStaticMapUrl(pins, portrait);
        if (u) allUrls.add(u);
      }
    });
    const imgCache = {};
    const urlList = Array.from(allUrls);
    await loadImagesBounded(urlList, imgCache, (done, total) => setProgLabel(`Loading images… ${done}/${total}`));
    const cachedImg = url => (url ? (imgCache[url] ?? null) : null);

    let imgFailed = 0;
    const imgSlides = slides.filter(s => s.type === "place").length;
    setFailedImgs(0);
    setTotalImgSlides(imgSlides);

    // A timeslice (not a bare start()) so ondataavailable fires periodically
    // instead of only once at stop() — some WebKit versions need this to
    // actually flush encoded data; relying solely on the final chunk
    // produced a 0-byte file on iOS Safari (confirmed 2026-09-30: recording
    // completed and downloaded, but was empty).
    recorder.start(1000);

    if (titleSlide) {
      setProgLabel("Title slide");
      const titleImg = cachedImg(titleBgUrl);
      // One-time text layout — the redraw closure below just replays the
      // same draw calls with these already-computed values, it doesn't
      // recompute wrapping every 200ms.
      const label = titleTextRef.current.trim() || titleText.trim() || "Trip";
      const fontSize = 96, lineH = fontSize * 1.2, maxW = CANVAS_W - 200;
      ctx2d.font = `bold ${fontSize}px Arial,sans-serif`;
      const words = label.split(" ");
      const lines = []; let line = "";
      for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (ctx2d.measureText(test).width > maxW && line) { lines.push(line); line = word; } else line = test;
      }
      if (line) lines.push(line);
      const totalH = lines.length * lineH;
      const startY = CANVAS_H / 2 - totalH / 2 + lineH / 2;

      await holdFrame(() => {
        if (titleImg) {
          drawCover(ctx2d, titleImg, CANVAS_W, CANVAS_H);
          const ov = ctx2d.createLinearGradient(0, 0, 0, CANVAS_H);
          ov.addColorStop(0, "rgba(0,0,0,0.40)"); ov.addColorStop(1, "rgba(0,0,0,0.68)");
          ctx2d.fillStyle = ov; ctx2d.fillRect(0, 0, CANVAS_W, CANVAS_H);
        } else {
          const bg = ctx2d.createLinearGradient(0, 0, CANVAS_W, CANVAS_H);
          bg.addColorStop(0, "#1e3a5f"); bg.addColorStop(1, "#0f172a");
          ctx2d.fillStyle = bg; ctx2d.fillRect(0, 0, CANVAS_W, CANVAS_H);
        }
        ctx2d.font = `bold ${fontSize}px Arial,sans-serif`;
        ctx2d.fillStyle = "#fff"; ctx2d.textAlign = "center"; ctx2d.textBaseline = "middle";
        lines.forEach((l, i) => ctx2d.fillText(l, CANVAS_W / 2, startY + i * lineH));
        ctx2d.font = "24px Arial,sans-serif"; ctx2d.fillStyle = "rgba(255,255,255,0.40)";
        ctx2d.textAlign = "right"; ctx2d.textBaseline = "top";
        ctx2d.fillText("Andiamo Places", CANVAS_W - 24, 20);
      }, duration * 1000);
    }

    for (let i = 0; i < slides.length; i++) {
      setProgress(i / slides.length);
      const slide = slides[i];

      if (slide.type === "map") {
        setProgLabel("Map overview");
        const pins   = allPlaces.filter(p => selIds.has(String(p.ID)));
        const mapUrl = buildStaticMapUrl(pins, portrait);
        const img    = cachedImg(mapUrl);
        await holdFrame(() => {
          if (img) { drawCover(ctx2d, img, CANVAS_W, CANVAS_H); drawMapOverlay(ctx2d, CANVAS_W); }
          else      drawPlaceholder(ctx2d, CANVAS_W, CANVAS_H, "Trip Map", "");
        }, duration * 1000);
      } else if (slide.type === "daymap") {
        const dateStr = formatSlideDate(slide.date);
        setProgLabel(`Day map — ${dateStr}`);
        const mapUrl = buildStaticMapUrl(slide.places, portrait);
        const img    = cachedImg(mapUrl);
        await holdFrame(() => {
          if (img) { drawCover(ctx2d, img, CANVAS_W, CANVAS_H); drawDayMapOverlay(ctx2d, CANVAS_W, CANVAS_H, dateStr); }
          else      drawPlaceholder(ctx2d, CANVAS_W, CANVAS_H, dateStr, "Day Overview");
        }, duration * 1000);
      } else {
        const { place, date } = slide;
        const name    = place.Name || "Place";
        const dateStr = formatSlideDate(date);
        setProgLabel(`"${name}"`);
        const url = resolveImg(place.Photo_m || place.Photo);
        const img = cachedImg(url);
        if (!img && url) { imgFailed++; setFailedImgs(imgFailed); }
        await holdFrame(() => {
          if (img) { drawCover(ctx2d, img, CANVAS_W, CANVAS_H); drawOverlay(ctx2d, CANVAS_W, CANVAS_H, name, dateStr); }
          else      drawPlaceholder(ctx2d, CANVAS_W, CANVAS_H, name, dateStr);
        }, duration * 1000);
      }
    }

    if (endCard) {
      setProgLabel("Attribution");
      const endImg = cachedImg(endCardBgUrl);
      await holdFrame(() => drawEndCard(ctx2d, endImg, CANVAS_W, CANVAS_H), duration * 1000);
    }

    // "Finalizing" — assembling the Blob from every captured chunk
    // (finishBlob) is a synchronous browser call that can genuinely take a
    // couple of seconds for a longer recording (more chunks, since start()
    // now uses a 1s timeslice), during which the whole page — not just the
    // Download button — stops responding to hover/clicks. Setting this
    // label now, before that freeze starts, means it's already painted on
    // screen when the freeze hits, instead of the UI just looking dead for
    // a few seconds with no explanation. Found 2026-09-30.
    setProgLabel("Finalizing video…");
    // See finishBlob's own comment — guards against onstop never firing at
    // all (observed on iOS Chrome: progress reached ~98%, every slide was
    // drawn, this stop() call ran, and the recorder just never produced a
    // stop event). 8s is generous for flushing whatever's already been
    // captured; finishBlob itself clears this if onstop fires normally.
    stopWatchdogRef.current = setTimeout(finishBlob, 8000);
    recorder.stop();
    audio.stop();
  }, [allPlaces, selIds, duration, audioTrack, mapPos, dailyMaps, skipNoPhoto, titleSlide, titleBg, titleText, endCard, fileBaseName, guide_meta, CANVAS_W, CANVAS_H, portrait]);

  const handleClose = () => {
    if (audioRef.current) { audioRef.current.stop(); audioRef.current = null; }
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    onHide();
  };

  const hasPhoto    = p => !!resolveImg(p.Photo_m || p.Photo);
  const selCount    = allPlaces.filter(p => selIds.has(String(p.ID))).length;
  const videoCount  = allPlaces.filter(p => selIds.has(String(p.ID)) && (!skipNoPhoto || hasPhoto(p))).length;
  const totalSlides = videoCount + (mapPos !== "off" ? 1 : 0) + (titleSlide ? 1 : 0) + (endCard ? 1 : 0);
  const estSecs     = Math.round(totalSlides * duration); // 1.5s slides → fractional
  const estLabel    = estSecs < 60
    ? `~${estSecs}s`
    : `~${Math.floor(estSecs / 60)}m ${estSecs % 60}s`;

  const isSupported = !!(window.MediaRecorder && HTMLCanvasElement.prototype.captureStream);

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="ev-dialog">
      <Modal.Body style={{ padding: 0 }}>
        <div className="ev-shell">

          {/* Header */}
          <div className="ev-header">
            <div className="ev-header-icon"><FontAwesomeIcon icon={solid("film")} /></div>
            <h2 className="ev-title">Export as Video</h2>
            <button className="ev-close" onClick={handleClose} aria-label="Close">
              <FontAwesomeIcon icon={solid("xmark")} />
            </button>
          </div>

          {/* Canvas — off-screen when not generating, shown as preview while recording */}
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            className={`ev-canvas${phase === "generating" ? " ev-canvas--on" : ""}${portrait ? " ev-canvas--portrait" : ""}`}
          />

          {/* ── Settings ── */}
          {phase === "settings" && (
            <div className="ev-body">

              {!isSupported && (
                <div className="ev-compat-warn">
                  <FontAwesomeIcon icon={solid("triangle-exclamation")} />
                  Video export requires a modern desktop browser (Chrome, Firefox, Edge). iOS Safari has limited support.
                </div>
              )}

              {errorMsg && (
                <div className="ev-compat-warn">
                  <FontAwesomeIcon icon={solid("triangle-exclamation")} />
                  {errorMsg}
                </div>
              )}

              <div className="ev-section">
                <div className="ev-label">Format</div>
                <div className="ev-pills">
                  {Object.entries(FORMATS).map(([id, f]) => (
                    <button key={id} className={`ev-pill${format === id ? " ev-pill--on" : ""}`} onClick={() => setFormat(id)}>
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ev-section">
                <div className="ev-label">Slide duration</div>
                <div className="ev-pills">
                  {DURATION_OPTIONS.map(d => (
                    <button key={d} className={`ev-pill${duration === d ? " ev-pill--on" : ""}`} onClick={() => setDuration(d)}>
                      {d}s
                    </button>
                  ))}
                </div>
              </div>

              <div className="ev-section">
                <div className="ev-label">Background audio</div>
                <div className="ev-pills">
                  {AUDIO_TRACKS.map(t => (
                    <button key={t.id} className={`ev-pill${audioTrack === t.id ? " ev-pill--on" : ""}`} onClick={() => setAudioTrack(t.id)}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ev-section">
                <div className="ev-label">Map slide</div>
                <div className="ev-pills">
                  {MAP_OPTIONS.map(o => (
                    <button key={o.id} className={`ev-pill${mapPos === o.id ? " ev-pill--on" : ""}`} onClick={() => setMapPos(o.id)}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="ev-section">
                <label className="ev-check-row">
                  <input type="checkbox" className="ev-cb" checked={dailyMaps} onChange={e => setDailyMaps(e.target.checked)} />
                  <span className="ev-check-label">Include a map slide per day</span>
                </label>
                <label className="ev-check-row" style={{ marginTop: 6 }}>
                  <input type="checkbox" className="ev-cb" checked={skipNoPhoto} onChange={e => setSkipNoPhoto(e.target.checked)} />
                  <span className="ev-check-label">Skip places without a photo</span>
                </label>
              </div>

              <div className="ev-section">
                <label className="ev-check-row">
                  <input type="checkbox" className="ev-cb" checked={titleSlide} onChange={e => setTitleSlide(e.target.checked)} />
                  <span className="ev-check-label">Include a title slide</span>
                </label>
                {titleSlide && (<>
                  <div className="ev-label" style={{ marginTop: 8 }}>Title text</div>
                  <input
                    className="ev-title-input"
                    value={titleText}
                    onChange={e => { setTitleText(e.target.value); titleTextRef.current = e.target.value; }}
                    placeholder="Title text…"
                  />
                  <div className="ev-pills" style={{ marginTop: 8 }}>
                    <button className={`ev-pill${titleBg === "photo" ? " ev-pill--on" : ""}`} onClick={() => setTitleBg("photo")}>Photo background</button>
                    <button className={`ev-pill${titleBg === "blank" ? " ev-pill--on" : ""}`} onClick={() => setTitleBg("blank")}>Blank background</button>
                  </div>
                </>)}
              </div>

              <div className="ev-section">
                <label className="ev-check-row">
                  <input type="checkbox" className="ev-cb" checked={endCard} onChange={e => setEndCard(e.target.checked)} />
                  <span className="ev-check-label">Include an attribution screen at the end</span>
                </label>
              </div>

              <div className="ev-section">
                <div className="ev-label">File name</div>
                <div className="ev-filename-row">
                  <input
                    className="ev-filename-input"
                    value={fileBaseName}
                    onChange={e => setFileBaseName(e.target.value.replace(/[/\\:*?"<>|]/g, ""))}
                    spellCheck={false}
                  />
                  <span className="ev-filename-ext">.webm</span>
                </div>
              </div>

              <div className="ev-section ev-section--list">
                <div className="ev-list-header">
                  <span className="ev-label">
                    Places ({selCount}&thinsp;/&thinsp;{allPlaces.length})
                    {skipNoPhoto && videoCount < selCount && (
                      <span className="ev-skip-hint">&ensp;·&ensp;{videoCount} in video</span>
                    )}
                  </span>
                  <button
                    className="ev-toggle-btn"
                    onClick={() => selCount === allPlaces.length
                      ? setSelIds(new Set())
                      : setSelIds(new Set(allPlaces.map(p => String(p.ID))))
                    }
                  >
                    {selCount === allPlaces.length ? "Select None" : "Select All"}
                  </button>
                </div>
                <div className="ev-list">
                  {allPlaces.map(p => {
                    const id     = String(p.ID);
                    const imgUrl = resolveImg(p.Photo_m || p.Photo);
                    const date   = dateMap[id];
                    return (
                      <label key={id} className={`ev-place-row${skipNoPhoto && !imgUrl ? " ev-place-row--skip" : ""}`}>
                        <input type="checkbox" className="ev-cb" checked={selIds.has(id)} onChange={() => togglePlace(id)} />
                        <div className={`ev-thumb${imgUrl ? "" : " ev-thumb--empty"}`}>
                          {imgUrl && (
                            <img
                              src={imgUrl}
                              alt=""
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                // Either field (Photo_m or Photo) can be the broken
                                // one depending on the place, and either resolution
                                // strategy (proxied or direct-https) can be the one
                                // that works — try all four combinations, in order,
                                // before giving up.
                                const tried = (e.target.dataset.tried || "").split("|").filter(Boolean);
                                tried.push(e.target.src);
                                const candidates = [
                                  resolveImg(p.Photo_m),
                                  directImg(p.Photo_m),
                                  resolveImg(p.Photo),
                                  directImg(p.Photo),
                                ].filter((u) => u && !tried.includes(u));
                                if (candidates.length) {
                                  e.target.dataset.tried = tried.join("|");
                                  e.target.src = candidates[0];
                                } else {
                                  e.target.onerror = null;
                                  e.target.style.display = "none";
                                  e.target.parentElement.classList.add("ev-thumb--empty");
                                }
                              }}
                            />
                          )}
                        </div>
                        <div className="ev-place-info">
                          <span className="ev-place-name">{p.Name}</span>
                          {date && <span className="ev-place-date">{formatSlideDate(date)}</span>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="ev-footer">
                <span className="ev-estimate">{totalSlides} slides&ensp;·&ensp;{estLabel}</span>
                <button className="ev-gen-btn" onClick={handleGenerate} disabled={totalSlides === 0 || !isSupported}>
                  <FontAwesomeIcon icon={solid("video")} />
                  Generate
                </button>
              </div>
            </div>
          )}

          {/* ── Generating ── */}
          {phase === "generating" && (
            <div className="ev-gen-body">
              <div className="ev-prog-label">{progLabel}</div>
              <div className="ev-prog-track">
                <div className="ev-prog-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
              <div className="ev-prog-pct">{Math.round(progress * 100)}%</div>
              <div className="ev-prog-hint">Recording in progress — keep this window open</div>
            </div>
          )}

          {/* ── Done ── */}
          {phase === "done" && (
            <div className="ev-done">
              <div className="ev-done-check"><FontAwesomeIcon icon={solid("circle-check")} /></div>
              <div className="ev-done-title">Video ready</div>
              <div className="ev-done-file">{fileName}</div>
              {failedImgs > 0 && (
                <div className="ev-done-warn">
                  <FontAwesomeIcon icon={solid("triangle-exclamation")} />
                  {" "}{failedImgs} of {totalImgSlides} photo{totalImgSlides !== 1 ? "s" : ""} couldn't be loaded — shown as placeholders
                </div>
              )}
              <div className="ev-done-actions">
                <button className="ev-gen-btn" onClick={() => {
                  if (!blobUrl) return;
                  const a = document.createElement("a");
                  a.href = blobUrl; a.download = fileName; a.click();
                  // User-requested: the dialog's job is done once the
                  // download is triggered — staying open just adds an extra
                  // manual close step. handleClose (not a bare onHide) so
                  // the same audio/recorder cleanup every other close path
                  // uses still runs.
                  //
                  // Deferred, not called synchronously right after a.click()
                  // — Chrome hadn't actually started processing the blob-URL
                  // download yet when handleClose's own cleanup (stopping
                  // the Web Audio engine, unmounting this whole dialog/
                  // canvas tree) ran in the same tick, blocking the main
                  // thread for a few seconds right as the click was still
                  // being handled (symptom: Download button unresponsive,
                  // wrong cursor). A short delay lets the browser's own
                  // download handling actually begin first. Found
                  // 2026-09-30.
                  setTimeout(handleClose, 300);
                }}>
                  <FontAwesomeIcon icon={solid("download")} />
                  Download
                </button>
                <button className="ev-another-btn" onClick={() => setPhase("settings")}>
                  Export another
                </button>
              </div>
            </div>
          )}

        </div>
      </Modal.Body>
    </Modal>
  );
}
